"use client";

import {
  createContext,
  useCallback,
  useContext,
  useState,
  useRef,
  useEffect,
} from "react";
import { cn } from "@/lib/utils";

type ToastMessage = { id: number; text: string; type?: "success" | "error" };

type ToastContextValue = {
  toast: (text: string, type?: "success" | "error") => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) return { toast: () => {} };
  return ctx;
}

const DURATION_MS = 4000;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [messages, setMessages] = useState<ToastMessage[]>([]);
  const idRef = useRef(0);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const toast = useCallback((text: string, type: "success" | "error" = "success") => {
    const id = ++idRef.current;
    setMessages((prev) => [...prev, { id, text, type }]);
    const t = setTimeout(() => {
      setMessages((prev) => prev.filter((m) => m.id !== id));
    }, DURATION_MS);
    timeoutsRef.current.push(t);
  }, []);

  useEffect(() => {
    const timeouts = timeoutsRef.current;
    return () => timeouts.forEach(clearTimeout);
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div
        className="fixed bottom-4 left-4 right-4 z-[100] flex flex-col gap-2 pointer-events-none sm:left-auto sm:right-4 sm:max-w-sm"
        aria-live="polite"
        aria-atomic="true"
      >
        {messages.map((m) => (
          <div
            key={m.id}
            className={cn(
              "rounded-lg border px-4 py-3 text-sm font-medium shadow-lg transition-opacity pointer-events-auto",
              m.type === "error"
                ? "border-destructive/50 bg-destructive/10 text-destructive"
                : "border-primary/30 bg-card text-foreground"
            )}
            role="status"
          >
            {m.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
